import React from "react";
import { Box, Typography, makeStyles } from "@material-ui/core";
import iconExclusivePPV from "../../../Main/Landing/images/Vault/exclusive-ppv.png";
import iconPrivateCreator from "../../../Main/Landing/images/Vault/private-creator.png";
import iconCustomVideo from "../../../Main/Landing/images/Vault/custom-video-req.png";
import iconBehindScenes from "../../../Main/Landing/images/Vault/behind-the-scenes.png";
import iconPremiumCreator from "../../../Main/Landing/images/Vault/premium-creator.png";
import iconEarlyAccess from "../../../Main/Landing/images/Vault/early-access.png";

const useStyles = makeStyles((theme) => ({
    featuresSection: {
        padding: '50px 5%',
        backgroundColor: '#111111',
        [theme.breakpoints.down('sm')]: {
            padding: '30px 5%',
        }
    },
    featuresContainer: {
        maxWidth: 1314,
        margin: '0 auto',
        boxSizing: 'border-box',
    },
    featuresHeader: {
        textAlign: 'left',
        marginBottom: '40px',
        [theme.breakpoints.down('sm')]: {
            marginBottom: '18px',
        }
    },
    featuresTitle: {
        fontFamily: '"Cormorant Garamond", serif',
        fontSize: '52px',
        fontWeight: 400,
        lineHeight: '100%',
        textAlign: "center",
        color: '#ffffff',
        marginBottom: '16px',
        [theme.breakpoints.down('sm')]: {
            fontSize: '36px',
            lineHeight: '44px',
        }
    },
    featuresSubtitle: {
        fontFamily: "Roboto, Arial, sans-serif",
        fontSize: '18px',
        textAlign: "center",
        fontWeight: 200,
        lineHeight: '27px',
        color: '#ffffff',
    },
    gridContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        [theme.breakpoints.up('md')]: {
            borderTop: '1px solid rgba(210, 219, 230, 0.1)',
            borderBottom: '1px solid rgba(210, 219, 230, 0.1)',
            borderImageSource:
                'linear-gradient(90deg, rgba(135, 149, 156, 0.1) 1.44%, rgba(137, 149, 156, 0.1) 7.69%, rgba(139, 148, 155, 0.1) 13.94%, rgba(177, 179, 187, 0.1) 21.15%, rgba(195, 200, 204, 0.1) 31.25%, rgba(241, 242, 244, 0.1) 38.94%, rgba(210, 219, 230, 0.1) 51.44%, rgba(210, 219, 230, 0.1) 63.46%, rgba(154, 176, 183, 0.1) 81.73%, rgba(165, 183, 192, 0.1) 94.71%)',
            borderImageSlice: 1,
        },
    },
    gridItem: {
        padding: '24px 20px',
        cursor: 'pointer',
        boxSizing: 'border-box',
        transition: 'all 0.3s ease',
        '&:hover': {
            backgroundColor: '#0a0a0a',
            [theme.breakpoints.up('md')]: {
                borderLeftColor: 'transparent',
            },
        },
        [theme.breakpoints.up('md')]: {
            borderLeft: '1px solid rgba(210, 219, 230, 0.1)',
            '&:nth-child(3n)': {
                borderRight: '1px solid rgba(210, 219, 230, 0.1)',
            },
            '&:not(:nth-last-child(-n+3))': {
                borderBottom: '1px solid rgba(210, 219, 230, 0.1)',
            },
        },
        [theme.breakpoints.down('sm')]: {
            padding: '24px 0px',
            borderBottom: '1px solid rgba(255,255,255,0.12)',
            borderLeft: 'none',
            '&:last-child': {
                borderBottom: 'none',
            },
        },
    },
    gridItemIcon: {
        width: 40,
        height: 40,
        marginBottom: 32,
        display: 'block',
        objectFit: 'contain',
    },
    gridItemTitle: {
        fontSize: '24px',
        fontWeight: 500,
        lineHeight: '24px',
        fontFamily: '"Cormorant Garamond", serif',
        color: '#ffffff',
        marginBottom: '12px',
        [theme.breakpoints.down('sm')]: {
            fontSize: '20px',
            lineHeight: '100%',
        }
    },
    gridItemDesc: {
        fontSize: '18px',
        fontWeight: 200,
        color: '#ffffff',
        lineHeight: '27px',
        [theme.breakpoints.down('sm')]: {
            fontSize: '16px',
            lineHeight: '24px',
        }
    }
}));

const FEATURES_DATA = [
    { title: "Exclusive PPV Content", desc: "Pay-per-view releases you won't find anywhere else on the platform.", icon: iconExclusivePPV },
    { title: "Private Creator Feeds", desc: "A quieter, subscriber-only stream from every creator you follow.", icon: iconPrivateCreator },
    { title: "Custom Video Requests", desc: "Ask directly, and have a creator bring your request to life.", icon: iconCustomVideo },
    { title: "Behind-the-scenes Content", desc: "The parts of the process that never make the main feed.", icon: iconBehindScenes },
    { title: "Premium Creator Collections", desc: "Curated bundles, priced and packaged by the creators themselves.", icon: iconPremiumCreator },
    { title: "Early Access Releases", desc: "See new drops before they reach the wider platform.", icon: iconEarlyAccess }
];

export const VaultPremiumSection: React.FC = () => {
    const classes = useStyles();

    return (
        <Box className={classes.featuresSection}>
            <Box className={classes.featuresContainer}>
                <Box className={classes.featuresHeader}>
                    <Typography variant="h2" className={classes.featuresTitle}>
                        Premium Experiences
                    </Typography>
                    <Typography className={classes.featuresSubtitle}>
                        Every membership carries the same standard of access. No tiers to climb, no fine print to read twice.
                    </Typography>
                </Box>

                <Box className={classes.gridContainer}>
                    {FEATURES_DATA.map((feature, index) => (
                        <Box
                            key={index}
                            width={{ xs: '100%', md: '33.333%' }}
                            className={classes.gridItem}
                        >
                            <img src={feature.icon} alt={feature.title} className={classes.gridItemIcon} />
                            <Typography className={classes.gridItemTitle}>
                                {feature.title}
                            </Typography>
                            <Typography className={classes.gridItemDesc}>
                                {feature.desc}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
};
